import React, { useEffect, useState } from "react";
import { Search, Plus, Trash2, Phone, Mail, ArrowRight, X, LayoutGrid, List, Zap } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { toast } from "sonner";
import { fetchLeads, createLead, updateLeadApi, deleteLeadApi, pushToDisputeFox } from "../../lib/api";
import { leadStatuses, services } from "../../mock";
import { useAuth } from "../../context/AuthContext";

const statusColor = (s) =>
  ({
    New: "bg-blue-100 text-blue-700",
    Contacted: "bg-amber-100 text-amber-700",
    Qualified: "bg-purple-100 text-purple-700",
    Converted: "bg-green-100 text-green-700",
    Lost: "bg-gray-100 text-gray-500",
  }[s] || "bg-gray-100 text-gray-600");

export default function Leads() {
  const { token } = useAuth();
  const [leads, setLeads] = useState([]);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("All");
  const [open, setOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [view, setView] = useState(localStorage.getItem("rfl_leads_view") || "grid");
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "", source: "Manual" });

  const changeView = (v) => {
    setView(v);
    localStorage.setItem("rfl_leads_view", v);
  };

  const refresh = async () => setLeads(await fetchLeads());

  useEffect(() => {
    refresh();
  }, []);

  const filtered = leads.filter((l) => {
    const matchQ =
      l.name.toLowerCase().includes(query.toLowerCase()) ||
      l.email.toLowerCase().includes(query.toLowerCase());
    const matchF = filter === "All" || l.status === filter;
    return matchQ && matchF;
  });

  const changeStatus = async (id, status) => {
    await updateLeadApi(id, { status });
    await refresh();
    toast.success(`Lead moved to ${status}`);
  };

  const pushLead = async (l) => {
    try {
      await pushToDisputeFox(token, { name: l.name, email: l.email, phone: l.phone || "", service: l.service || "", event: "manual_push_lead" });
      toast.success(`${l.name} sent to DisputeFox`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not push to DisputeFox");
    }
  };

  const handleAdd = async () => {
    if (!form.name || !form.email) {
      toast.error("Name and email are required");
      return;
    }
    await createLead(form);
    await refresh();
    setOpen(false);
    setForm({ name: "", email: "", phone: "", service: "", message: "", source: "Manual" });
    toast.success("Lead added");
  };

  const confirmDelete = async () => {
    await deleteLeadApi(deleteId);
    await refresh();
    setDeleteId(null);
    toast.success("Lead deleted");
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-2xl font-bold text-navy">Leads</h1>
          <p className="text-gray-500 text-sm mt-0.5">
            {leads.length} total · captured from website &amp; manual entry
          </p>
        </div>
        <Button onClick={() => setOpen(true)} className="bg-navy hover:bg-navy-light text-white">
          <Plus className="h-4 w-4 mr-1.5" /> Add Lead
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name or email..."
            className="pl-10 bg-white"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {["All", ...leadStatuses].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`px-3.5 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === s ? "bg-navy text-white" : "bg-white text-gray-600 hover:bg-gray-100"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
        <div className="flex gap-1 bg-white rounded-lg p-1 self-start">
          <button
            onClick={() => changeView("grid")}
            className={`h-8 w-8 flex items-center justify-center rounded-md transition-colors ${view === "grid" ? "bg-navy text-white" : "text-gray-500 hover:bg-gray-100"}`}
            data-testid="leads-view-grid"
            aria-label="Grid view"
          >
            <LayoutGrid className="h-4 w-4" />
          </button>
          <button
            onClick={() => changeView("list")}
            className={`h-8 w-8 flex items-center justify-center rounded-md transition-colors ${view === "list" ? "bg-navy text-white" : "text-gray-500 hover:bg-gray-100"}`}
            data-testid="leads-view-list"
            aria-label="List view"
          >
            <List className="h-4 w-4" />
          </button>
        </div>
      </div>

      {view === "list" ? (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden" data-testid="leads-list-view">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-left text-xs uppercase tracking-wide text-gray-400">
                  <th className="px-4 py-3 font-medium">Name</th>
                  <th className="px-4 py-3 font-medium">Contact</th>
                  <th className="px-4 py-3 font-medium">Service</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((l) => (
                  <tr key={l.id} className="border-b border-gray-50 last:border-0 hover:bg-cream/40">
                    <td className="px-4 py-3">
                      <div className="font-medium text-navy">{l.name}</div>
                      <div className="text-xs text-gray-400">{l.source}</div>
                    </td>
                    <td className="px-4 py-3">
                      <a href={`mailto:${l.email}`} className="block text-gray-600 hover:text-navy break-all">{l.email}</a>
                      {l.phone && <a href={`tel:${l.phone}`} className="block text-xs text-gray-400 hover:text-navy">{l.phone}</a>}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{l.service || "—"}</td>
                    <td className="px-4 py-3">
                      <Select value={l.status} onValueChange={(v) => changeStatus(l.id, v)}>
                        <SelectTrigger className="h-8 text-xs w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {leadStatuses.map((s) => (
                            <SelectItem key={s} value={s}>{s}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button
                          onClick={() => pushLead(l)}
                          title="Push to DisputeFox"
                          className="h-8 w-8 inline-flex items-center justify-center rounded-lg text-gray-400 hover:bg-orange-50 hover:text-orange-500 transition-colors"
                          data-testid={`lead-push-disputefox-${l.id}`}
                        >
                          <Zap className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setDeleteId(l.id)}
                          className="h-8 w-8 inline-flex items-center justify-center rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-500 transition-colors"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-12 text-center text-gray-400">No leads match your filters.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.map((l) => (
          <div key={l.id} className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-full bg-navy text-gold font-semibold flex items-center justify-center">
                  {l.name.split(" ").map((n) => n[0]).join("").slice(0, 2)}
                </div>
                <div>
                  <div className="font-semibold text-navy">{l.name}</div>
                  <div className="text-xs text-gray-400">{l.source}</div>
                </div>
              </div>
              <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusColor(l.status)}`}>
                {l.status}
              </span>
            </div>
            <div className="mt-4 space-y-1.5 text-sm text-gray-600">
              <a href={`mailto:${l.email}`} className="flex items-center gap-2 hover:text-navy break-all">
                <Mail className="h-4 w-4 text-gray-400" /> {l.email}
              </a>
              {l.phone && (
                <a href={`tel:${l.phone}`} className="flex items-center gap-2 hover:text-navy">
                  <Phone className="h-4 w-4 text-gray-400" /> {l.phone}
                </a>
              )}
            </div>
            {l.service && (
              <div className="mt-3 inline-block bg-cream text-navy text-xs px-2.5 py-1 rounded-md">
                {l.service}
              </div>
            )}
            {l.message && (
              <p className="mt-3 text-sm text-gray-500 line-clamp-2">{l.message}</p>
            )}
            <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between gap-2">
              <Select value={l.status} onValueChange={(v) => changeStatus(l.id, v)}>
                <SelectTrigger className="h-9 text-sm flex-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {leadStatuses.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <button
                onClick={() => pushLead(l)}
                title="Push to DisputeFox"
                className="h-9 w-9 flex items-center justify-center rounded-lg text-gray-400 hover:bg-orange-50 hover:text-orange-500 transition-colors"
                data-testid={`lead-push-disputefox-grid-${l.id}`}
              >
                <Zap className="h-4 w-4" />
              </button>
              <button
                onClick={() => setDeleteId(l.id)}
                className="h-9 w-9 flex items-center justify-center rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-500 transition-colors"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="col-span-full bg-white rounded-xl p-12 text-center text-gray-400 border border-dashed border-gray-200">
            No leads match your filters.
          </div>
        )}
      </div>
      )}

      {/* Add Lead Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-navy">Add New Lead</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Input placeholder="Full name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <Input placeholder="Email *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <Input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
              <SelectTrigger><SelectValue placeholder="Service of interest" /></SelectTrigger>
              <SelectContent>
                {services.map((s) => <SelectItem key={s.id} value={s.title}>{s.title}</SelectItem>)}
              </SelectContent>
            </Select>
            <Textarea placeholder="Notes / message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={3} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={handleAdd} className="bg-navy hover:bg-navy-light text-white">Add Lead</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this lead?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-500 hover:bg-red-600">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
